import dayjs from 'dayjs';
import { CALENDAR_CONSTANTS } from './constants';
import { timeToPixels } from './timeHelpers';

export const calculateEventPosition = (event, startHour, hourHeight = CALENDAR_CONSTANTS.HOUR_HEIGHT) => {
  const startTime = dayjs(event.startTime);
  const endTime = dayjs(event.endTime);

  const startTimeStr = startTime.format('HH:mm');
  const endTimeStr = endTime.format('HH:mm');

  const top = timeToPixels(startTimeStr, startHour, hourHeight);
  const bottom = timeToPixels(endTimeStr, startHour, hourHeight);
  const height = Math.max(bottom - top, CALENDAR_CONSTANTS.HOUR_HEIGHT / 4);

  return {
    top,
    height,
    startTime: startTimeStr,
    endTime: endTimeStr,
  };
};

/**
 * Check if two events overlap in time
 */
const eventsOverlap = (event1, event2) => {
  const start1 = dayjs(event1.startTime);
  const end1 = dayjs(event1.endTime);
  const start2 = dayjs(event2.startTime);
  const end2 = dayjs(event2.endTime);

  return start1.isBefore(end2) && start2.isBefore(end1);
};

/**
 * Find all events that overlap with a given event (directly or transitively)
 */
const findOverlapGroup = (event, events, visited = new Set()) => {
  const eventKey = event.id || `${event.startTime}-${event.endTime}`;
  if (visited.has(eventKey)) return [];

  visited.add(eventKey);
  const group = [event];

  for (const other of events) {
    const otherKey = other.id || `${other.startTime}-${other.endTime}`;
    if (visited.has(otherKey)) continue;

    if (eventsOverlap(event, other)) {
      group.push(...findOverlapGroup(other, events, visited));
    }
  }

  return group;
};

/**
 * Assign columns to events within an overlap group
 */
const assignColumnsToGroup = (group) => {
  // Sort by start time, then by duration (longer first)
  const sorted = [...group].sort((a, b) => {
    const startDiff = dayjs(a.startTime).diff(dayjs(b.startTime));
    if (startDiff !== 0) return startDiff;
    // Longer events first
    const durationA = dayjs(a.endTime).diff(dayjs(a.startTime));
    const durationB = dayjs(b.endTime).diff(dayjs(b.startTime));
    return durationB - durationA;
  });

  const columns = []; // Array of arrays, each containing events in that column
  const eventColumns = new Map();

  for (const event of sorted) {
    let placed = false;

    // Try to find a column where this event doesn't overlap with existing events
    for (let colIndex = 0; colIndex < columns.length; colIndex++) {
      const column = columns[colIndex];
      const canPlace = column.every(existing => !eventsOverlap(event, existing));

      if (canPlace) {
        column.push(event);
        eventColumns.set(event.id || `${event.startTime}-${event.endTime}`, colIndex);
        placed = true;
        break;
      }
    }

    // Create new column if needed
    if (!placed) {
      columns.push([event]);
      eventColumns.set(event.id || `${event.startTime}-${event.endTime}`, columns.length - 1);
    }
  }

  return { columns, eventColumns, totalColumns: columns.length };
};

export const detectOverlappingEvents = (events) => {
  if (!events || events.length === 0) return new Map();

  const eventPositions = new Map();
  const processed = new Set();

  for (const event of events) {
    const eventKey = event.id || `${event.startTime}-${event.endTime}`;
    if (processed.has(eventKey)) continue;

    // Find all events in this overlap group
    const group = findOverlapGroup(event, events, new Set());

    // Mark all as processed
    group.forEach(e => processed.add(e.id || `${e.startTime}-${e.endTime}`));

    if (group.length === 1) {
      // No overlaps - full width
      eventPositions.set(eventKey, {
        columnIndex: 0,
        totalColumns: 1,
        width: '100%',
        left: '0',
      });
    } else {
      // Has overlaps - distribute into columns
      const { eventColumns, totalColumns } = assignColumnsToGroup(group);

      for (const [key, colIndex] of eventColumns) {
        eventPositions.set(key, {
          columnIndex: colIndex,
          totalColumns,
          width: `${100 / totalColumns}%`,
          left: `${(colIndex * 100) / totalColumns}%`,
        });
      }
    }
  }

  return eventPositions;
};

export const getEventsForDay = (events, dayDate) => {
  if (!events || !dayDate) return [];

  const targetDate = dayjs(dayDate).format('YYYY-MM-DD');

  return events.filter((event) => {
    const eventDate = dayjs(event.startTime).format('YYYY-MM-DD');
    return eventDate === targetDate;
  });
};

export const sortEventsByTime = (events) => {
  return [...events].sort((a, b) => {
    return dayjs(a.startTime).diff(dayjs(b.startTime));
  });
};

export const groupEventsByDay = (events) => {
  const grouped = {};

  events.forEach((event) => {
    const dayKey = dayjs(event.startTime).format('YYYY-MM-DD');
    if (!grouped[dayKey]) {
      grouped[dayKey] = [];
    }
    grouped[dayKey].push(event);
  });

  Object.keys(grouped).forEach((key) => {
    grouped[key] = sortEventsByTime(grouped[key]);
  });

  return grouped;
};

export const calculateGridHeight = (startHour, endHour, hourHeight = CALENDAR_CONSTANTS.HOUR_HEIGHT) => {
  const totalHours = endHour - startHour;
  return totalHours * hourHeight;
};